/**
 * RSVP deadline reminder, REST over `/planning/:project_id/rsvp-reminder`.
 *
 * Preview first, then send: the preview returns a token for exactly the list it showed, and a send must carry that
 * token, so nobody is messaged who was not on the screen. Nothing is cached or kept in localStorage, and no phone
 * number or provider detail ever comes back from the API.
 */
import { apiGet, apiPost, planningPath } from './PlanningApiClient';
import { isApiError } from '../utils/apiErrors';
import { PROVIDER_NOT_CONFIGURED_MESSAGE } from './CommunicationsService';
import type { RsvpReminderPreview, RsvpReminderResult } from '../types/RsvpReminder';

const TEMPLATE_NOT_CONFIGURED_MESSAGE =
  'The reminder message template has not been approved and added yet. Ask an admin to set it up before sending.';
const SEND_IN_PROGRESS_MESSAGE = 'A reminder is already being sent for this project. Wait for it to finish.';
const LIST_CHANGED_MESSAGE = 'The list changed since you previewed it. Review it again before sending.';

/** An error whose `message` is safe to show as is. */
export class RsvpReminderError extends Error {
  code: string | null;
  /** The guest list moved under the screen: re-read the preview. */
  listChanged: boolean;

  constructor(message: string, code: string | null = null) {
    super(message);
    this.name = 'RsvpReminderError';
    this.code = code;
    this.listChanged = code === 'list_changed';
  }
}

export function mapRsvpReminderError(err: unknown): RsvpReminderError {
  if (err instanceof RsvpReminderError) return err;
  if (!isApiError(err)) return new RsvpReminderError(err instanceof Error && err.message ? err.message : 'That did not go through. Please try again.');
  switch (err.code) {
    case 'list_changed': return new RsvpReminderError(LIST_CHANGED_MESSAGE, err.code);
    case 'template_not_configured': return new RsvpReminderError(TEMPLATE_NOT_CONFIGURED_MESSAGE, err.code);
    case 'provider_not_configured': return new RsvpReminderError(PROVIDER_NOT_CONFIGURED_MESSAGE, err.code);
    case 'send_in_progress': return new RsvpReminderError(SEND_IN_PROGRESS_MESSAGE, err.code);
    default: return new RsvpReminderError(err.message, err.code);
  }
}

interface RawPreview {
  configured?: boolean;
  not_configured_reason?: 'template_not_configured' | 'provider_not_configured' | null;
  eligible?: number;
  will_send?: number;
  excluded?: { replied?: number; not_invited?: number; no_phone?: number; recently_reminded?: number };
  sample?: string | null;
  batch_token?: string;
}

export async function loadRsvpReminderPreview(deadline: string, signal?: AbortSignal): Promise<RsvpReminderPreview> {
  try {
    const raw = await apiGet<RawPreview>(planningPath(`/rsvp-reminder?deadline=${encodeURIComponent(deadline)}`), signal);
    return {
      configured: raw.configured === true,
      notConfiguredReason: raw.not_configured_reason ?? null,
      eligible: raw.eligible ?? 0,
      willSend: raw.will_send ?? 0,
      excluded: {
        replied: raw.excluded?.replied ?? 0,
        notInvited: raw.excluded?.not_invited ?? 0,
        noPhone: raw.excluded?.no_phone ?? 0,
        recentlyReminded: raw.excluded?.recently_reminded ?? 0,
      },
      sample: raw.sample ?? null,
      batchToken: raw.batch_token ?? '',
    };
  } catch (err) {
    throw mapRsvpReminderError(err);
  }
}

export async function sendRsvpReminder(deadline: string, batchToken: string): Promise<RsvpReminderResult> {
  try {
    const raw = await apiPost<{ sent?: number; failed?: number; skipped?: number; stopped_early?: boolean; remaining?: number }>(
      planningPath('/rsvp-reminder/send'),
      { deadline, batch_token: batchToken },
    );
    return {
      sent: raw.sent ?? 0,
      failed: raw.failed ?? 0,
      skipped: raw.skipped ?? 0,
      stoppedEarly: raw.stopped_early === true,
      remaining: raw.remaining ?? 0,
    };
  } catch (err) {
    throw mapRsvpReminderError(err);
  }
}
